import React, { useMemo } from 'react';
import {
  Box,
  Heading,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
  TimeField,
} from '../ui';
import type {
  Changelog_HandledByCoaFragment,
  Changelog_HandledByCoaHistoryFragment,
  Changelog_HandledBySpotFragment,
  Changelog_HandledBySpotHistoryFragment,
  Changelog_LocationFragment,
  Changelog_ModifiedByUserFragment,
  Changelog_PortCallFragment,
  Changelog_PortCallHistoryFragment,
  Changelog_PortNoticeFragment,
  Changelog_PortNoticeHistoryFragment,
  Changelog_StatementOfFactsFragment,
  Changelog_StatementOfFactsHistoryFragment,
  Changelog_VoyageCharterHistoryFragment,
  Changelog_VoyageChartersFragment,
} from '../../graphql';
import { useChangelogQuery } from '../../graphql';
import type { Column, Row, TableState } from 'react-table';
import { useSortBy, useTable } from 'react-table';
import moment from 'moment';
import { isDefined, mapKeyBy } from '../../lib/util';
import compact from 'lodash/compact';
import type { ClientId } from '../core/ClientId';
import LoadingSpinner from '../LoadingSpinner';

interface ChangelogProps {
  portCallClientIds: ClientId[];
  portCallDbIds: number[];
  voyageCharterIds: number[];
}

interface PortCallChangeRow
  extends Omit<Changelog_PortCallHistoryFragment, '__typename'> {
  start: number | null;
}

interface PortNoticeChangeRow
  extends Omit<Changelog_PortNoticeHistoryFragment, '__typename'> {
  start: number | null;
}

interface StatementOfFactsChangeRow
  extends Omit<Changelog_StatementOfFactsHistoryFragment, '__typename'> {
  start: number | null;
}

interface VoyageCharterChangeRow
  extends Omit<Changelog_VoyageCharterHistoryFragment, '__typename'> {
  start: number | null;
  ship: string | null;
}

interface HandledByCoaChangeRow
  extends Omit<Changelog_HandledByCoaHistoryFragment, '__typename'> {
  start: number | null;
  ship: string | null;
}

interface HandledBySpotChangeRow
  extends Omit<Changelog_HandledBySpotHistoryFragment, '__typename'> {
  start: number | null;
  ship: string | null;
}

type ChangeRow =
  | PortCallChangeRow
  | PortNoticeChangeRow
  | StatementOfFactsChangeRow
  | VoyageCharterChangeRow
  | HandledByCoaChangeRow
  | HandledBySpotChangeRow;

type Sources =
  | 'Port call planning'
  | 'Port notice'
  | 'Statement of facts'
  | 'Voyage charter'
  | 'Spot schedule'
  | 'Coa schedule';

interface ChangelogRowData {
  date: number | null;
  user: Changelog_ModifiedByUserFragment | null;
  location?: Changelog_LocationFragment | null;
  changes: Array<{ key: string; value: unknown }>;
  source?: Sources;
}

const humanReadableProperties: Record<string, string> = {
  plannedArrival: 'Planned arrival',
  plannedArrivalDuration: 'Planned arrival duration',
  plannedArrivalOverride: 'Planned arrival override',
  plannedBerthing: 'Planned berthing',
  plannedBerthingDuration: 'Planned berthing duration',
  plannedBerthingOverride: 'Planned berthing override',
  plannedDeparture: 'Planned departure',
  plannedDepartureDuration: 'Planned departure duration',
  plannedDepartureOverride: 'Planned departure override',
  seaDurationOverride: 'Voyage duration',
  ataBerth: 'Berthed',
  ataPbp: 'End of sea passage',
  berthingProspects: 'ETB',
  eta: 'ETA',
  etdBerth: 'ETD',
  norTendered: 'Notice given at',
  arrivalTime: 'Arrival',
  berthed: 'Berthed',
  loadingCommenced: 'Loading/discharge commenced',
  loadingCompleted: 'Loading/discharge completed',
  noticeOfReadiness: 'Notice of readiness',
  pilotOnboard: 'Pilot onboard',
  sailingTime: 'Sailing time',
  shared: 'Published',
  ship: 'Vessel',
};

const getPortCallRow = ({
  id,
  plannedArrival,
  plannedArrivalDuration,
  plannedArrivalOverride,
  plannedBerthing,
  plannedBerthingDuration,
  plannedBerthingOverride,
  plannedDeparture,
  plannedDepartureDuration,
  plannedDepartureOverride,
  seaDurationOverride,
  userByModifiedByUserId,
  sysPeriod,
}:
  | Changelog_PortCallFragment
  | Changelog_PortCallHistoryFragment): PortCallChangeRow => ({
  id,
  plannedArrival,
  plannedArrivalDuration,
  plannedArrivalOverride,
  plannedBerthing,
  plannedBerthingDuration,
  plannedBerthingOverride,
  plannedDeparture,
  plannedDepartureDuration,
  plannedDepartureOverride,
  seaDurationOverride,
  userByModifiedByUserId,
  sysPeriod,
  start: sysPeriod?.start?.value
    ? new Date(sysPeriod.start.value).getTime()
    : null,
});

const getPortNoticeRow = ({
  ataBerth,
  ataPbp,
  berthingProspects,
  eta,
  etdBerth,
  id,
  norTendered,
  portCallId,
  userByModifiedByUserId,
  sysPeriod,
}:
  | Changelog_PortNoticeFragment
  | Changelog_PortNoticeHistoryFragment): PortNoticeChangeRow => ({
  ataBerth,
  ataPbp,
  berthingProspects,
  eta,
  etdBerth,
  id,
  norTendered,
  portCallId,
  userByModifiedByUserId,
  sysPeriod,
  start: sysPeriod?.start?.value
    ? new Date(sysPeriod.start.value).getTime()
    : null,
});

const getSofRow = ({
  arrivalTime,
  berthed,
  id,
  loadingCommenced,
  loadingCompleted,
  noticeOfReadiness,
  pilotOnboard,
  portCallId,
  sailingTime,
  userByModifiedByUserId,
  sysPeriod,
}:
  | Changelog_StatementOfFactsFragment
  | Changelog_StatementOfFactsHistoryFragment): StatementOfFactsChangeRow => ({
  arrivalTime,
  berthed,
  id,
  loadingCommenced,
  loadingCompleted,
  noticeOfReadiness,
  pilotOnboard,
  portCallId,
  sailingTime,
  userByModifiedByUserId,
  sysPeriod,
  start: sysPeriod?.start?.value
    ? new Date(sysPeriod.start.value).getTime()
    : null,
});

const getVcRow = ({
  id,
  userByModifiedByUserId,
  sysPeriod,
  shipByVesselId,
}:
  | Changelog_VoyageChartersFragment
  | Changelog_VoyageCharterHistoryFragment): VoyageCharterChangeRow => ({
  id,
  shipByVesselId,
  ship: shipByVesselId?.name ?? null,
  userByModifiedByUserId,
  sysPeriod,
  start: sysPeriod?.start?.value
    ? new Date(sysPeriod.start.value).getTime()
    : null,
});

const getHbcRow = ({
  id,
  userByModifiedByUserId,
  sysPeriod,
  shared,
  shipByVesselId,
}:
  | Changelog_HandledByCoaFragment
  | Changelog_HandledByCoaHistoryFragment): HandledByCoaChangeRow => ({
  id,
  shared,
  shipByVesselId,
  ship: shipByVesselId?.name ?? null,
  userByModifiedByUserId,
  sysPeriod,
  start: sysPeriod?.start?.value
    ? new Date(sysPeriod.start.value).getTime()
    : null,
});

const getHbsRow = ({
  id,
  userByModifiedByUserId,
  sysPeriod,
  shared,
  shipByVesselId,
}:
  | Changelog_HandledBySpotFragment
  | Changelog_HandledBySpotHistoryFragment): HandledBySpotChangeRow => ({
  id,
  shared,
  shipByVesselId,
  ship: shipByVesselId?.name ?? null,
  userByModifiedByUserId,
  sysPeriod,
  start: sysPeriod?.start?.value
    ? new Date(sysPeriod.start.value).getTime()
    : null,
});

const sortByStart = <T extends ChangeRow>(rowA: T, rowB: T): number => {
  const startA = rowA.start;
  const startB = rowB.start;

  if (startA && (startB === null || startA > startB)) return 1;
  if (startB && (startA === null || startA < startB)) return -1;

  return 0;
};

const shallowDiffKeys = <T, K extends keyof T>(
  oldObj: T | undefined,
  newObj: T,
  keys: Array<K>,
): Partial<Pick<T, K>> => {
  const retObj: Partial<T> = {};

  keys.forEach((key) => {
    if (newObj[key] !== (oldObj?.[key] ?? null)) {
      retObj[key] = newObj[key];
    }
  });

  return retObj;
};

const getChanges = <
  Row extends Pick<ChangeRow, 'start' | 'userByModifiedByUserId'>,
  Key extends Exclude<keyof Row, 'start' | 'userByModifiedByUserId'>,
>(
  rows: Row[],
  keys: Key[],
  source?: Sources,
): Array<Pick<ChangelogRowData, 'changes' | 'date' | 'user'>> =>
  rows
    .map((curRow, i) => {
      const prevRow = i !== 0 ? rows[i - 1] : undefined;
      const rowChanges = shallowDiffKeys(prevRow, curRow, keys);

      const changedValues = Object.entries(rowChanges).map(([key, value]) => ({
        key,
        value: value ?? null,
      }));

      if (changedValues.length === 0) {
        console.warn(
          'Could not determine changes between rows',
          curRow,
          prevRow,
          'checked keys',
          keys,
        );
        return null;
      }

      return {
        changes: changedValues,
        date: curRow.start,
        user: curRow.userByModifiedByUserId ?? null,
        source,
      };
    })
    .filter(isDefined);

const columns: Array<Column<ChangelogRowData>> = [
  {
    id: 'date',
    accessor: (row) => row.date,
    Header: 'Date',
    Cell: ({ value }: { value: number }) => <TimeField value={value} />,
  },
  {
    id: 'user',
    accessor: (row) => row.user?.name ?? 'User not available',
    Header: 'Modified by',
  },
  {
    id: 'location',
    accessor: (row) => row.location?.name ?? 'N/A',
    Header: 'Location',
  },
  {
    id: 'source',
    accessor: (row) => row.source ?? '',
    Header: 'Source',
  },
  {
    id: 'changes',
    accessor: (row) => row.changes,
    Header: 'Changes',
    Cell: ({
      value: rowValue,
      row,
    }: {
      value: ChangelogRowData['changes'];
      row: Row<ChangelogRowData>;
    }) => {
      return (
        <Box display="flex" flexDirection="column" flexWrap="wrap">
          {rowValue.map(({ key, value }, i) => {
            const isDate =
              typeof value === 'string' &&
              moment(value, moment.ISO_8601, true).isValid();

            return (
              <Text key={i} isSingleLine mr={3}>
                <>
                  {humanReadableProperties[key] ?? key}:{' '}
                  {typeof value === 'string' && isDate ? (
                    <TimeField
                      value={value}
                      timeZone={row.original.location?.timezone ?? 'UTC'}
                    />
                  ) : value === true ? (
                    'Yes'
                  ) : value === false ? (
                    'No'
                  ) : (
                    value
                  )}
                </>
              </Text>
            );
          })}
        </Box>
      );
    },
  },
];

const Changelog = ({
  portCallClientIds,
  portCallDbIds,
  voyageCharterIds,
}: ChangelogProps): JSX.Element => {
  const {
    loading: changelogQueryLoading,
    data: changelogQueryData,
    error: changelogQueryError,
  } = useChangelogQuery({
    variables: {
      portCallIds: portCallDbIds,
      portCallClientIds,
      voyageCharterIds,
    },
    fetchPolicy: 'no-cache',
  });

  const data = useMemo(() => {
    const portCallsById = mapKeyBy(
      compact(changelogQueryData?.allPortCalls?.nodes),
      (pc) => pc.id,
    );

    const portCallRowsGrouped = changelogQueryData?.allPortCalls?.nodes.map(
      (pc) =>
        [pc, ...(pc?.portCallHistoriesById?.nodes ?? [])]
          .filter(isDefined)
          .map((rows) => getPortCallRow(rows))
          .sort(sortByStart),
    );

    const portNoticeRowsGrouped = changelogQueryData?.allPortNotices?.nodes.map(
      (pn) =>
        [pn, ...(pn?.portNoticeHistoriesById?.nodes ?? [])]
          .filter(isDefined)
          .map((rows) => getPortNoticeRow(rows))
          .sort(sortByStart),
    );

    const sofRowsGrouped = changelogQueryData?.allStatementOfFacts?.nodes.map(
      (sof) =>
        [sof, ...(sof?.statementOfFactsHistoriesById?.nodes ?? [])]
          .filter(isDefined)
          .map((rows) => getSofRow(rows))
          .sort(sortByStart),
    );

    const vcRowsGrouped = changelogQueryData?.allVoyageCharters?.nodes.map(
      (vc) =>
        [vc, ...(vc?.voyageCharterHistoriesById?.nodes ?? [])]
          .filter(isDefined)
          .map((rows) => getVcRow(rows))
          .sort(sortByStart),
    );

    const hbcRowsGrouped = changelogQueryData?.allVoyageCharters?.nodes
      .filter(isDefined)
      .flatMap((vc) =>
        vc.orderVoyageCharterRelationsByVoyageCharterId.nodes
          .filter(isDefined)
          .map((ovcr) => {
            const hbc = ovcr.orderByOrderId?.handledByCoaByOrderId;
            return [hbc, ...(hbc?.handledByCoaHistoriesById?.nodes ?? [])]
              .filter(isDefined)
              .map((rows) => getHbcRow(rows))
              .sort(sortByStart);
          }),
      );

    const hbsRowsGrouped = changelogQueryData?.allVoyageCharters?.nodes
      .filter(isDefined)
      .flatMap((vc) =>
        vc.orderVoyageCharterRelationsByVoyageCharterId.nodes
          .filter(isDefined)
          .map((ovcr) => {
            const hbs = ovcr.orderByOrderId?.handledBySpotByOrderId;
            return [hbs, ...(hbs?.handledBySpotHistoriesById?.nodes ?? [])]
              .filter(isDefined)
              .map((rows) => getHbsRow(rows))
              .sort(sortByStart);
          }),
      );

    const portCallChanges =
      portCallRowsGrouped?.flatMap((changesByPortCall) => {
        const changes = getChanges(
          changesByPortCall,
          [
            'plannedArrival',
            'plannedArrivalDuration',
            'plannedArrivalOverride',
            'plannedBerthing',
            'plannedBerthingDuration',
            'plannedBerthingOverride',
            'plannedDeparture',
            'plannedDepartureDuration',
            'plannedDepartureOverride',
            'seaDurationOverride',
          ],
          'Port call planning',
        );

        const location =
          portCallsById.get(changesByPortCall[0].id)?.locationByLocationId ??
          null;

        return changes.map((change) => ({
          ...change,
          location,
        }));
      }) ?? [];

    const portNoticeChanges =
      portNoticeRowsGrouped?.flatMap((rowsByPortNotice) => {
        const changes = getChanges(
          rowsByPortNotice,
          [
            'ataBerth',
            'ataPbp',
            'berthingProspects',
            'eta',
            'etdBerth',
            'norTendered',
          ],
          'Port notice',
        );

        const location =
          portCallsById.get(rowsByPortNotice[0].portCallId)
            ?.locationByLocationId ?? null;

        return changes.map((change) => ({
          ...change,
          location,
        }));
      }) ?? [];

    const sofChanges =
      sofRowsGrouped?.flatMap((rowsBySof) => {
        const changes = getChanges(
          rowsBySof,
          [
            'arrivalTime',
            'berthed',
            'loadingCommenced',
            'loadingCompleted',
            'noticeOfReadiness',
            'pilotOnboard',
            'sailingTime',
          ],
          'Statement of facts',
        );

        const location =
          portCallsById.get(rowsBySof[0].portCallId)?.locationByLocationId ??
          null;

        return changes.map((change) => ({
          ...change,
          location,
        }));
      }) ?? [];

    const vcChanges =
      vcRowsGrouped?.flatMap((rowsByVc) =>
        getChanges(rowsByVc, ['ship'], 'Voyage charter'),
      ) ?? [];

    const hbcChanges =
      hbcRowsGrouped?.flatMap((rowsByHbc) =>
        getChanges(rowsByHbc, ['shared', 'ship'], 'Coa schedule'),
      ) ?? [];

    const hbsChanges =
      hbsRowsGrouped?.flatMap((rowsByHbs) =>
        getChanges(rowsByHbs, ['shared', 'ship'], 'Spot schedule'),
      ) ?? [];

    return [
      ...portCallChanges,
      ...portNoticeChanges,
      ...sofChanges,
      ...vcChanges,
      ...hbcChanges,
      ...hbsChanges,
    ].filter(
      (change) => (change?.date ?? -1) > 0, // Filter out changes with no proper date (entries created before history table implementation)
    );
  }, [changelogQueryData]);

  const initialState = useMemo<Partial<TableState<ChangelogRowData>>>(
    () => ({
      sortBy: [{ id: 'date', desc: true }],
    }),
    [],
  );

  const { getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<ChangelogRowData>(
      {
        columns,
        data,
        initialState,
      },
      useSortBy,
    );

  return (
    <Box p={4}>
      <Heading mb={4} fontWeight="bold" fontSize={4}>
        Voyage changelog
      </Heading>
      {changelogQueryLoading && (
        <Box display="flex" flexDirection="row" p={4}>
          <LoadingSpinner />
          <Text ml={3}>Loading voyage changelog</Text>
        </Box>
      )}
      {changelogQueryError && (
        <Box display="flex" flexDirection="row" p={4}>
          <Text ml={3} color="orange.orange_600">
            Error loading voyage changelog
          </Text>
        </Box>
      )}
      {!changelogQueryLoading && !changelogQueryError && (
        <Box borderColor="neutral.gray_225" borderWidth={1} borderStyle="solid">
          <Table data-testid="voyage-changelog-table">
            <thead>
              {headerGroups.map((headerGroup) => (
                // eslint-disable-next-line react/jsx-key
                <TableHeaders isSticky {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    // eslint-disable-next-line react/jsx-key
                    <TableHeader {...column.getHeaderProps()}>
                      {column.render('Header')}
                    </TableHeader>
                  ))}
                </TableHeaders>
              ))}
            </thead>
            <tbody {...getTableBodyProps}>
              {rows.map((row, index) => {
                prepareRow(row);
                const { key, ...rowProps } = row.getRowProps();
                return (
                  <TableRow
                    data-testid={`changelog-${key}`}
                    key={key}
                    isMuted={index % 2 !== 0}
                    {...rowProps}
                  >
                    {row.cells.map((cell) => {
                      const { key, ...cellProps } = cell.getCellProps();
                      return (
                        <TableCell key={key} {...cellProps}>
                          {cell.render('Cell')}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default Changelog;
